import { useState } from 'react';
import { ApiFailure, api } from '../lib/api';
import { getPosition } from '../lib/native';

export interface Place {
  id: string;
  name: string;
  address: string;
  lat: number;
  lng: number;
  isDefault: boolean;
}

export interface RouteOptions {
  /** 手上的单都来自同一个取货点时,它的名字;否则 null */
  pickup: { name: string } | null;
  pickupCount: number;
  places: Place[];
}

/** 终点选「回到起点」时的取值 */
export const BACK_TO_START = 'BACK_TO_START';

/**
 * 智能推荐路线的起点和终点。
 *
 * ★ 终点为什么要能选:原来固定「回到起点」(Google 必须给终点),
 *   而司机送完最后一单往往直接回家 —— 按回起点排,最后几站是朝着店的方向绕的。
 *   让他存几个常去的地方(家、车库),规划时挑一个,路线就朝那里排。
 *
 * ★ 起点默认**现在的位置**:路线是从他人在哪儿开始跑的,按取货点排的话,
 *   人已经在路上了、第一站却按「从店里出发」挑,前几站是绕回去的。
 *   取货点仍然能选 —— 在家提前排、或者店里定位飘的时候用。
 *   单子来自好几个取货点时取货点选不了。
 */
export function RouteEndpoints({
  options,
  onPlacesChanged,
  start,
  setStart,
  endId,
  setEndId,
}: {
  options: RouteOptions;
  onPlacesChanged: (places: Place[]) => void;
  start: 'PICKUP' | 'CURRENT';
  setStart: (s: 'PICKUP' | 'CURRENT') => void;
  endId: string;
  setEndId: (id: string) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [confirmDel, setConfirmDel] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function reload(): Promise<Place[]> {
    const places = await api<Place[]>('/driver/places');
    onPlacesChanged(places);
    return places;
  }

  async function makeDefault(id: string) {
    setErr(null);
    try {
      await api(`/driver/places/${id}`, { method: 'PATCH', body: { isDefault: true } });
      await reload();
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '没改成');
    }
  }

  async function remove(id: string) {
    setErr(null);
    try {
      await api(`/driver/places/${id}`, { method: 'DELETE' });
      const places = await reload();
      setConfirmDel(null);
      // 删的正是选中的那个:换成新的默认,没有就回到起点
      if (endId === id) setEndId(places.find((p) => p.isDefault)?.id ?? BACK_TO_START);
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '没删掉');
    }
  }

  const radio = (checked: boolean) =>
    `flex w-full items-start gap-2 rounded-lg border px-3 py-2 text-left ${
      checked
        ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10'
        : 'border-slate-200 dark:border-slate-700'
    }`;

  return (
    <div className="space-y-3">
      <fieldset className="space-y-1.5">
        <legend className="mb-1 text-xs font-medium text-slate-500">起点</legend>
        <button
          type="button"
          role="radio"
          aria-checked={start === 'CURRENT'}
          onClick={() => setStart('CURRENT')}
          className={radio(start === 'CURRENT')}
        >
          <span className="text-sm text-slate-900 dark:text-slate-100">
            我现在的位置
            <span className="block text-xs text-slate-500">从你此刻所在的地方开始排(要打开定位)</span>
          </span>
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={start === 'PICKUP'}
          disabled={!options.pickup}
          onClick={() => setStart('PICKUP')}
          className={`${radio(start === 'PICKUP')} disabled:opacity-50`}
        >
          <span className="text-sm text-slate-900 dark:text-slate-100">
            取货点{options.pickup ? `:${options.pickup.name}` : ''}
            <span className="block text-xs text-slate-500">
              {options.pickup
                ? '在家提前排、或者店里定位不准时选这个'
                : options.pickupCount > 1
                  ? `手上的单来自 ${options.pickupCount} 个取货点,没法用取货点当起点`
                  : '取货点没有坐标,没法当起点'}
            </span>
          </span>
        </button>
      </fieldset>

      <fieldset className="space-y-1.5">
        <legend className="mb-1 text-xs font-medium text-slate-500">终点(送完最后一单去哪)</legend>
        {options.places.map((p) => (
          <div key={p.id} className={radio(endId === p.id)}>
            <button
              type="button"
              role="radio"
              aria-checked={endId === p.id}
              aria-label={`终点:${p.name}`}
              onClick={() => setEndId(p.id)}
              className="min-w-0 flex-1 text-left"
            >
              <span className="text-sm text-slate-900 dark:text-slate-100">
                {p.name}
                {p.isDefault && (
                  <span className="ml-1.5 rounded bg-brand-600 px-1.5 py-0.5 text-[10px] text-white">
                    默认
                  </span>
                )}
                <span className="block truncate text-xs text-slate-500">{p.address}</span>
              </span>
            </button>
            <div className="flex shrink-0 flex-col items-end gap-1 text-xs">
              {!p.isDefault && (
                <button type="button" onClick={() => void makeDefault(p.id)} className="text-brand-600">
                  设为默认
                </button>
              )}
              {confirmDel === p.id ? (
                <button type="button" onClick={() => void remove(p.id)} className="text-red-600">
                  确认删除
                </button>
              ) : (
                <button type="button" onClick={() => setConfirmDel(p.id)} className="text-slate-400">
                  删除
                </button>
              )}
            </div>
          </div>
        ))}
        <button
          type="button"
          role="radio"
          aria-checked={endId === BACK_TO_START}
          onClick={() => setEndId(BACK_TO_START)}
          className={radio(endId === BACK_TO_START)}
        >
          <span className="text-sm text-slate-900 dark:text-slate-100">
            回到起点
            <span className="block text-xs text-slate-500">送完要回店里就选这个</span>
          </span>
        </button>
        {adding ? (
          <PlaceForm
            submitLabel="保存并选用"
            onCancel={() => setAdding(false)}
            onSubmit={async (v) => {
              const p = await api<Place>('/driver/places', { method: 'POST', body: v });
              await reload();
              setEndId(p.id);
              setAdding(false);
            }}
          />
        ) : (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="w-full rounded-lg border border-dashed border-slate-300 px-3 py-2 text-sm text-brand-600
                       dark:border-slate-600"
          >
            + 新增终点
          </button>
        )}
      </fieldset>
      {err && <p className="text-xs text-red-600 dark:text-red-400">{err}</p>}
    </div>
  );
}

export interface PlaceValues {
  name: string;
  address: string;
  lat: number;
  lng: number;
  isDefault?: boolean;
}

/**
 * 终点的表单:名字 + 地址。新增和编辑共用(「我的」页和规划面板都用它)。
 *
 * 地址两种拿法,拿到的都是**坐标**:
 *   · 搜:用店里配的地理编码(和建单页同一家),挑一个结果 —— 地址和 GPS 一起带回来
 *   · 用我现在的位置:人就在那里时最省事,不用打字,也不怕地址写法搜不到
 *
 * 编辑时 initial 带着原来的地址,不动地址只改名字也行。
 */
export function PlaceForm({
  initial,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  initial?: { name: string; address: string; lat: number; lng: number };
  submitLabel: string;
  /** 抛 ApiFailure 的话表单里显示它 */
  onSubmit: (v: PlaceValues) => Promise<void>;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [q, setQ] = useState('');
  const [candidates, setCandidates] = useState<Array<{ address: string; lat: number; lng: number }>>([]);
  const [picked, setPicked] = useState<{ address: string; lat: number; lng: number } | null>(
    initial ? { address: initial.address, lat: initial.lat, lng: initial.lng } : null,
  );
  const [makeDefault, setMakeDefault] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function search() {
    if (q.trim().length < 2) return setErr('地址至少打两个字');
    setBusy('SEARCH');
    setErr(null);
    try {
      const r = await api<{ data: typeof candidates; reason: string | null }>(
        `/driver/places/search?q=${encodeURIComponent(q.trim())}`,
      );
      setCandidates(r.data);
      if (r.data.length === 0) setErr(r.reason ?? '没搜到这个地址 —— 换个写法,或者到了那里用「现在的位置」');
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '搜不了');
    } finally {
      setBusy(null);
    }
  }

  /* 人就在那个地方(比如到家了)时最省事:不用打字,也不怕地址写法搜不到 */
  async function useHere() {
    setBusy('HERE');
    setErr(null);
    // 不要缓存:存的是「此刻站的地方」
    const pos = await getPosition(10_000, 0);
    setBusy(null);
    if (!pos) return setErr('拿不到位置 —— 在手机设置里允许本应用定位');
    setCandidates([]);
    setQ('');
    setPicked({ address: `现在的位置(${pos.lat.toFixed(5)}, ${pos.lng.toFixed(5)})`, lat: pos.lat, lng: pos.lng });
  }

  async function save() {
    if (!name.trim()) return setErr('起个名字,比如「家」');
    if (!picked) return setErr('先搜一个地址挑一下,或者用现在的位置');
    setBusy('SAVE');
    setErr(null);
    try {
      await onSubmit({ name: name.trim(), ...picked, ...(makeDefault ? { isDefault: true } : {}) });
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '没存上');
      setBusy(null);
    }
  }

  const input =
    'h-10 w-full rounded-lg border border-slate-300 px-3 text-sm dark:border-slate-600 dark:bg-slate-800';

  return (
    <div className="space-y-2 rounded-lg border border-slate-200 p-3 dark:border-slate-700">
      <input aria-label="终点名称" placeholder="名字,比如:家" value={name} onChange={(e) => setName(e.target.value)} className={input} maxLength={32} />
      <div className="flex gap-2">
        <input
          aria-label="终点地址"
          placeholder="搜地址"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            /*
             * ★ 一开始改地址,原来的坐标就作废。
             *   不清的话:在框里打了新地址、忘了从结果里挑一个就点保存 ——
             *   存下去的还是旧地址和旧坐标,而屏幕上看起来像是改了。
             *   必须从结果里重新挑一个(或者用现在的位置),新坐标才算数。
             */
            if (e.target.value.trim()) {
              setPicked(null);
              setCandidates([]);
            }
          }}
          onKeyDown={(e) => e.key === 'Enter' && void search()}
          className={input}
        />
        <button type="button" onClick={() => void search()} disabled={busy !== null} className="shrink-0 rounded-lg border border-slate-300 px-3 text-sm dark:border-slate-600">
          {busy === 'SEARCH' ? '搜着…' : '搜'}
        </button>
      </div>
      <button type="button" onClick={() => void useHere()} disabled={busy !== null} className="text-sm text-brand-600">
        {busy === 'HERE' ? '定位中…' : '用我现在的位置'}
      </button>
      {candidates.length > 0 && (
        <ul className="space-y-1">
          {candidates.map((c, i) => (
            <li key={i}>
              <button
                type="button"
                onClick={() => setPicked(c)}
                className={`w-full rounded px-2 py-1.5 text-left text-sm ${
                  picked === c ? 'bg-brand-50 text-brand-700 dark:bg-brand-500/10' : 'text-slate-700 dark:text-slate-300'
                }`}
              >
                {c.address}
              </button>
            </li>
          ))}
        </ul>
      )}
      {/* 地址现在是什么状态 —— 有没有坐标一眼看得出来 */}
      {picked ? (
        <p className="text-xs text-slate-600 dark:text-slate-300" aria-label="已选地址">
          已选:{picked.address}
          <span className="block text-slate-400">
            坐标 {picked.lat.toFixed(5)}, {picked.lng.toFixed(5)}
          </span>
        </p>
      ) : (
        <p className="text-xs text-amber-700 dark:text-amber-400" aria-label="已选地址">
          还没选定地址 —— 搜索后从结果里点一个,或者用现在的位置
          {initial && (
            <button
              type="button"
              onClick={() => {
                setPicked({ address: initial.address, lat: initial.lat, lng: initial.lng });
                setQ('');
                setCandidates([]);
              }}
              className="ml-2 text-brand-600"
            >
              恢复原地址
            </button>
          )}
        </p>
      )}
      {!initial && (
        <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
          <input type="checkbox" checked={makeDefault} onChange={(e) => setMakeDefault(e.target.checked)} />
          设为默认终点
        </label>
      )}
      {err && <p className="text-xs text-red-600 dark:text-red-400">{err}</p>}
      <div className="flex gap-2">
        {/* 没有坐标就存不了:一个没坐标的终点,路线根本没法朝它排 */}
        <button
          type="button"
          onClick={() => void save()}
          disabled={busy !== null || !picked || !name.trim()}
          className="h-10 flex-1 rounded-lg bg-brand-600 text-sm font-medium text-white disabled:opacity-50"
        >
          {busy === 'SAVE' ? '存着…' : submitLabel}
        </button>
        <button type="button" onClick={onCancel} className="h-10 rounded-lg border border-slate-300 px-4 text-sm dark:border-slate-600">
          取消
        </button>
      </div>
    </div>
  );
}
